import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSessionQCM } from '../../hooks/fetch_api_hook/useSessionQCM';
import { useResultatsQCM } from '../../hooks/fetch_api_hook/useResultatsQCM';
import { useSessionStream } from '../../hooks/useSessionStream';
import PodiumQCM from '../../components/VueQuestion/PodiumQCM';
import RichTextBlockEleve from '../../components/VueQuestion/RichTextBlockEleve';
import ProjectionHeader from '../../components/Projection/ProjectionHeader';
import AutoFitText from '../../components/Projection/AutoFitText';
import ChoicesGrid from '../../components/Projection/ChoicesGrid';
import RotationHint from '../../components/Projection/RotationHint';
import WaitingBand from '../../components/Projection/WaitingBand';
import ScanScreen from '../../components/Projection/ScanScreen';
import ProjectionReveal from '../../components/Projection/ProjectionReveal';
import ProjConfetti from '../../components/Projection/ProjConfetti';
import TanoProjo from '../../components/Projection/TanoProjo';
import ProjectionDistribution, { PanneauAppareil } from '../../components/Projection/ProjectionDistribution';
import { Smartphone, X } from 'lucide-react';
import { sansRappelBrouillon } from '../../utils/rappelBrouillon';
import ProjectionRevue from '../../components/Projection/ProjectionRevue';
import {
  ecranProjection, fluxOuvert, resultatsNecessaires, calculerPodiumRanks, carteRevue,
  paletteReussite, phaseQuestion,
} from './revueCorrection';
import { gesteFin, gestePrecedent } from './etapeSeance';
import { usePostApi } from '../../hooks/post_api_hook/usePostApi';
import { url } from '../../utils/confserveur';
import { QCM_NAVY_BG, QCM_GREEN, FONT_TITRE } from '../../utils/qcmTheme';
import { useJetonScan } from '../../hooks/useJetonScan';
import '../../styles/projectionFonts.css';

/**
 * Écran vidéoprojecteur d'une session cartes — bleuté sombre TanoEd (même
 * ambiance que le podium) : question en très grand (AutoFitText — TOUT est
 * toujours visible, jamais de scroll ni troncature), tuiles A-D avec la
 * lettre en vedette, règle de rotation affichée en permanence et bande des
 * prénoms en attente. Les SVG/images restent zoomables au clic
 * (ZoomableMedia). Le podium de fin est inchangé.
 *
 * Avant le lancement (session `en_attente`), l'écran ne montre aucune question :
 * il affiche la distribution des cartes et le QR que le téléphone scanne pour
 * se relier (chantier jeton, 2026-09-04). Le téléphone est le seul à piloter.
 */

/** Dernier écran de la séance, une fois la correction passée. Le paquet de
 *  cartes est au professeur : il revient trié. La seule sortie de la
 *  projection, ouverte dans l'onglet de la composition : vers la page
 *  Évaluations de la classe, où vivent les résultats et la notation. */
const EcranFin = ({ onQuitter }) => (
  <div className="h-screen w-full flex items-center justify-center p-8 overflow-hidden text-white" style={{ background: QCM_NAVY_BG }}>
    <div className="text-center space-y-4">
      <p className="text-5xl font-black" style={{ fontFamily: FONT_TITRE }}>Session terminée</p>
      <p className="text-xl font-semibold text-white/50">Merci pour votre participation !</p>
      <p className="text-2xl font-bold" style={{ color: QCM_GREEN }}>Rendez vos cartes dans l'ordre.</p>
      {onQuitter ? (
        <button
          type="button"
          onClick={onQuitter}
          className="mt-6 px-6 py-3 rounded-full text-sm font-bold text-white/70 hover:text-white hover:bg-white/10 transition"
          style={{ border: '1px solid rgba(255,255,255,.3)' }}
        >
          Quitter la projection
        </button>
      ) : null}
    </div>
  </div>
);

const ProjectionQCM = () => {
  const { idclasse, id } = useParams();
  const navigate = useNavigate();

  const [refreshKey, setRefreshKey] = useState(0);
  const { data: session } = useSessionQCM(id, refreshKey);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const [questionCourante, setQuestionCourante] = useState(null);
  const [reponses, setReponses] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [revealed, setRevealed] = useState(false);
  const [animated, setAnimated] = useState(false);
  // Confettis du reveal ≥ 70 % — montés ~7 s puis démontés (pluie one-shot)
  const [confettis, setConfettis] = useState(false);
  // Dernier événement `etape` reçu par le flux. Le serveur n'émet que les
  // CHANGEMENTS d'étape (D14) : l'état initial se lit dans la charge du GET de
  // la session, et le flux ne sert qu'à le suivre. D'où le repli ci-dessous, et
  // non un `useState(session?.etape)` qui naîtrait avant la réponse du GET.
  const [etapeFlux, setEtapeFlux] = useState(null);
  // Le scan est ouvert pour UNE question : l'ordre que le serveur a nommé dans
  // l'événement `scan_ouvert` (bouton Scan ! du téléphone). Un ordre, pas un
  // booléen : la question suivante est fermée par construction, sans remise à
  // zéro, et un événement en retard pour une autre question ne rouvre rien.
  const [scanOuvertOrdre, setScanOuvertOrdre] = useState(null);

  const statut = session?.statut || 'en_attente';
  const totalEleves = session?.nb_eleves || 0;
  const totalQuestions = session?.questions?.length || 0;

  // Le flux fait foi dès qu'il s'est exprimé — il ne parle qu'après le GET, et
  // toujours pour le contredire. Une seule source lue deux fois, sinon `etape`
  // et `revue_ordre` pourraient venir de deux endroits différents.
  const deroule = etapeFlux || session || {};
  const etape = deroule.etape || 'quiz';
  const revueOrdre = deroule.revue_ordre || 0;
  const ecran = ecranProjection({ statut, etape });

  // Résultats : chargés une seule fois, à l'entrée dans le premier écran de fin.
  // La navigation de revue se fait ensuite sur les données déjà en main —
  // `revue_ordre` n'entre pas dans la clé du fetch.
  const resultatsId = resultatsNecessaires({ statut, etape }) ? id : null;
  const { data: resultats, isError: resultatsEnErreur, erreur: resultatsErreur } = useResultatsQCM(resultatsId, refreshKey);

  const podiumRanks = useMemo(
    () => calculerPodiumRanks(resultats, session?.mode),
    [resultats, session?.mode],
  );

  const carte = useMemo(
    () => carteRevue(resultats, revueOrdre),
    [resultats, revueOrdre],
  );

  // Initialiser questionCourante depuis les données de session (projection ouverte en retard)
  useEffect(() => {
    const questionActive = session?.question_active || 0;
    if (questionActive > 0 && !questionCourante && session?.questions?.length > 0) {
      const q = session.questions[questionActive - 1];
      if (q) setQuestionCourante(q);
    }
  }, [session, questionCourante]);

  const handleReveal = useCallback(() => {
    setRevealed(true);
    setTimeout(() => setAnimated(true), 50);
  }, []);

  // ─── Le téléphone, avant lancement seulement ───

  // Vrai tant que le professeur n'a pas lancé le quizz depuis son téléphone.
  // `questionCourante` fait partie du test : la session relue au chargement
  // n'est jamais rafraîchie, mais une question qui arrive par le flux prouve à
  // elle seule que la séance est partie.
  const avantLancement = statut === 'en_attente' && !questionCourante;

  // « Un appareil est relié » : dit par la séance au chargement, puis par
  // l'événement `presence` du flux, poussé à la réclamation du jeton et au
  // détachement. Le flux fait foi dès qu'il a parlé.
  const [presenceFlux, setPresenceFlux] = useState(null);
  const appareilRelie = Boolean(presenceFlux?.appareil_relie ?? session?.presence?.appareil_relie);

  // Le QR : demandé, renouvelé toutes les 90 s, retiré dès qu'un appareil est
  // relié ; « Détacher » rouvre le cycle. Cf. hooks/useJetonScan.js.
  // Actif pendant TOUTE la séance, pas seulement avant le lancement (bug de
  // démo du 2026-09-12) : un téléphone perdu en plein quizz (jeton expiré,
  // appli fermée) doit pouvoir se relier depuis la projection, sinon la classe
  // est bloquée sans porte de sortie. Le hook ne demande rien tant qu'un
  // appareil est relié.
  const { jeton, relie, detacher, relier, erreurJeton, refuse } = useJetonScan({ sessionId: id, actif: statut !== 'terminee', appareilRelie });

  // « Relier un téléphone » en plein quizz : À LA DEMANDE, jamais automatique
  // (en mode « même écran », l'onglet de pilotage vit sur les cookies du prof,
  // « relié / détaché » ne veut rien dire pour lui, et un QR qui s'impose
  // couvrait l'énoncé sans rien à cliquer quand le serveur refusait le jeton).
  // Le panneau : détache l'appareil courant s'il y en a un, demande le QR,
  // montre le QR et le lien, ou la raison du refus, et se FERME (bouton, ou
  // tout seul quand un appareil se relie).
  const [panneauRelier, setPanneauRelier] = useState(false);
  // Le serveur a refusé le détachement : dit dans le panneau, jamais avalé.
  // Accepté : la présence passe à « détaché » ici même, sans attendre le flux,
  // dans le MÊME état que lui (son prochain événement l'écrase).
  const [detachementRefuse, setDetachementRefuse] = useState(null);
  // TOUJOURS détacher puis redemander un jeton neuf, quoi que la projection
  // croie de la présence (elle a pu manquer l'événement du téléphone qui
  // s'est relié, et réafficher un QR déjà consommé). Cf. useJetonScan.relier.
  const ouvrirRelier = useCallback(async () => {
    setPanneauRelier(true);
    setDetachementRefuse(null);
    const refus = await relier();
    setDetachementRefuse(refus);
    if (!refus) setPresenceFlux({ appareil_relie: false });
  }, [relier]);
  // Un appareil vient de se relier (la présence passe à vrai) : le panneau a
  // fait son travail, il se referme.
  const presencePrecedente = useRef(appareilRelie);
  useEffect(() => {
    if (appareilRelie && !presencePrecedente.current) setPanneauRelier(false);
    presencePrecedente.current = appareilRelie;
  }, [appareilRelie]);

  // Flux SSE partagé (hooks/useSessionStream) — la sémantique métier reste ici
  const handleStreamEvent = useCallback((eventType, data) => {
    if (eventType === 'question_change') {
      setQuestionCourante(data);
      setParticipants(data.participants || []);
      setReponses([]);
      setRevealed(false);
      setAnimated(false);
      setConfettis(false);
    } else if (eventType === 'reponse') {
      setReponses(data.detail || []);
    } else if (eventType === 'reveal') {
      setRevealed(true);
      setTimeout(() => setAnimated(true), 50);
    } else if (eventType === 'etape') {
      setEtapeFlux({ etape: data.etape, revue_ordre: data.revue_ordre });
    } else if (eventType === 'session_terminee') {
      // Le serveur ne fait PAS suivre l'étape `fin` par un événement `etape` :
      // il coupe le flux à `session_terminee` (phase 1). Sans cette ligne,
      // `etapeFlux` resterait bloqué sur la dernière étape connue — la revue —
      // et l'écran ne quitterait jamais la correction. `fin`/0 est exactement
      // ce que le serveur vient d'écrire, par `etape/` comme par `end/`.
      setEtapeFlux({ etape: 'fin', revue_ordre: 0 });
    } else if (eventType === 'presence') {
      setPresenceFlux(data);
    } else if (eventType === 'scan_ouvert') {
      // Le bouton Scan ! du téléphone, arbitré par le serveur, qui nomme la
      // question dans l'événement.
      if (data?.question_ordre !== undefined && data?.question_ordre !== null) {
        setScanOuvertOrdre(Number(data.question_ordre));
      }
    }
  }, []);

  // Le flux est ouvert dès `en_attente` : c'est lui qui apporte l'événement
  // `presence` et fait basculer l'écran quand le téléphone se relie. Il reste
  // la MÊME connexion au démarrage du quizz — le serveur enchaîne sur
  // `question_change` sans que rien n'ait à être rouvert ici.
  //
  // Il reste ouvert AUSSI sur une séance `terminee` en revue : c'est le seul
  // canal par lequel les « précédent / suivant » du téléphone atteignent la
  // correction rejouée depuis le tableau de bord (D11). Le serveur ne lui
  // envoie alors pas `session_terminee` (phase 1), donc rien ne l'arrête.
  useSessionStream({
    sessionId: id,
    enabled: fluxOuvert({ statut, etape }),
    onEvent: handleStreamEvent,
    onTerminated: refresh,
  });

  // Pilotage de la revue REJOUÉE depuis la projection (décision prof
  // 2026-09-01) : sur une séance close, il n'y a plus de scan en jeu — le
  // professeur est devant l'ordinateur, la projection doit se suffire. Même
  // chemin que le téléphone : POST /etape/ (le serveur arbitre, le flux SSE
  // redescend la position à tous les écrans) — la projection est juste un
  // doigt de plus sur le même métronome, aucune navigation locale.
  // Piège connu du dépôt : usePostApi expose doPost(charge, url), cet ordre-là.
  const [, doPostEtape] = usePostApi(null, null);
  const piloterRevue = useCallback((charge) => {
    doPostEtape(charge, `${url}sessions-qcm/${id}/etape/`);
  }, [doPostEtape, id]);

  const nbReponses = reponses.length;

  // Élèves qui n'ont pas encore répondu
  const nonRepondants = useMemo(() => {
    const reponsesSet = new Set(reponses.map(r => r.code_aruco));
    return participants
      .filter(p => !reponsesSet.has(p.code_aruco))
      .sort((a, b) => a.nom.localeCompare(b.nom));
  }, [participants, reponses]);

  // « Classe complète » = plus aucun PARTICIPANT en attente (même source que
  // les pills — nb_eleves compte les absents et ne serait jamais atteint).
  // Secours sur nb_eleves si le back n'a pas envoyé la liste des participants.
  const complet = participants.length > 0
    ? nbReponses > 0 && nonRepondants.length === 0
    : totalEleves > 0 && nbReponses >= totalEleves;

  // % de bonnes réponses + message (seuils historiques, couleurs claires pour fond navy)
  const pctCorrect = useMemo(() => {
    if (nbReponses === 0) return 0;
    const correct = reponses.filter(r => r.est_correcte).length;
    return Math.round((correct / nbReponses) * 100);
  }, [reponses, nbReponses]);

  const revealInfo = useMemo(() => paletteReussite(pctCorrect), [pctCorrect]);

  const questionOrdre = questionCourante?.question_ordre || 0;
  const bonneReponse = questionCourante?.bonne_reponse || questionCourante?.contenu?.bonne_reponse || '';

  // ─── Réflexion, puis scan (lot « levez vos cartes », 2026-09-19) ───
  // Le serveur sait-il ouvrir le scan ? Tant que la séance n'a pas la clé,
  // l'écran d'avant : les prénoms dès la question.
  const backSaitOuvrir = Boolean(session) && Object.prototype.hasOwnProperty.call(session, 'scan_ouvert_a');
  // Ouvert par le flux pour cette question, ou déjà ouvert quand la projection
  // arrive en retard : le GET porte l'instant de la question en cours.
  const scanOuvert = scanOuvertOrdre === questionOrdre
    || (Boolean(session?.scan_ouvert_a) && session?.question_active === questionOrdre);
  const phase = phaseQuestion({ revealed, scanOuvert, nbReponses, backSaitOuvrir });

  // Reveal ≥ 70 % → pluie de confettis pendant ~7 s (one-shot, se démonte seule)
  useEffect(() => {
    if (!revealed || pctCorrect < 70) return undefined;
    setConfettis(true);
    const t = setTimeout(() => setConfettis(false), 7000);
    return () => clearTimeout(t);
  }, [revealed, pctCorrect]);

  // ─── Écrans de fin de séance ───
  // C'est l'ÉTAPE qui les déclenche, plus le statut : depuis D3 la séance reste
  // `en_cours` pendant le podium et la revue pour garder le flux — et donc le
  // pilotage téléphone — vivant jusqu'au dernier geste.
  // Barre de pilotage du déroulé de fin, podium et revue, QUEL QUE SOIT le
  // statut (chantier jeton, 2026-09-04) : le téléphone pilote d'ordinaire,
  // mais s'il est fermé ou hors de portée, le PC doit pouvoir finir la
  // séance. Discrète, en bas à droite, même chemin que le téléphone
  // (POST /etape/, le serveur arbitre, le flux redescend à tous les écrans).
  const barrePilotage = (etapeCourante) => {
    const deroule = {
      etape: etapeCourante,
      revue_ordre: revueOrdre,
      podium_actif: session?.podium_actif,
      mode_correction: session?.mode_correction,
      totalQuestions,
    };
    const geste = gesteFin(deroule);
    const precedent = gestePrecedent(deroule);
    if (!geste && !precedent) return null;
    return (
      <div className="fixed bottom-5 right-5 z-20 flex items-center gap-2">
        {precedent ? (
          <button
            type="button"
            onClick={() => piloterRevue(precedent.charge)}
            className="px-4 py-2.5 rounded-full text-sm font-bold text-white/70 hover:text-white transition"
            style={{ background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.25)' }}
          >
            ← {precedent.libelle}
          </button>
        ) : null}
        {geste ? (
          <button
            type="button"
            onClick={() => piloterRevue(geste.charge)}
            className="px-5 py-2.5 rounded-full text-sm font-black text-white transition hover:opacity-90"
            style={{ background: QCM_GREEN }}
          >
            {geste.libelle}
          </button>
        ) : null}
      </div>
    );
  };

  // Les résultats (podium, revue) viennent d'un appel de la projection,
  // `GET resultats/`, qui peut échouer (jeton périmé et rejeu refusé, réseau) :
  // avant, `useApi` avalait l'erreur et l'écran passait à l'étape suivante
  // avec un contenu vide, sans un mot (constat prof du 2026-09-12). Un écran
  // vide en classe est pire qu'un message : on dit le code, et on réessaie.
  const ecranResultatsEnErreur = resultatsEnErreur ? (
    <div className="h-screen w-full flex items-center justify-center p-8 overflow-hidden text-white" style={{ background: QCM_NAVY_BG }} data-testid="resultats-erreur">
      <div className="text-center space-y-5">
        <p className="text-3xl font-black" style={{ fontFamily: FONT_TITRE }}>Chargement des résultats impossible</p>
        <p className="font-semibold text-white/50">
          {resultatsErreur?.statut ? `Le serveur a répondu ${resultatsErreur.statut}.` : 'Le serveur n\'a pas répondu.'}
        </p>
        <button
          type="button"
          onClick={refresh}
          className="px-6 py-2.5 rounded-full text-sm font-black text-white transition hover:opacity-90"
          style={{ background: QCM_GREEN }}
        >
          Réessayer
        </button>
      </div>
    </div>
  ) : null;

  if (ecran === 'revue') {
    if (ecranResultatsEnErreur) return <>{barrePilotage('revue')}{ecranResultatsEnErreur}</>;
    return (
      <>
        {barrePilotage('revue')}
        <ProjectionRevue
          titre={session?.titre}
          carte={carte}
          totalQuestions={totalQuestions}
          // pourquoi on attend : les résultats sont là mais la question n'y est pas
          attenteRaison={resultats && !carte ? `La question ${revueOrdre} n'est pas dans les résultats renvoyés par le serveur.` : null}
        />
      </>
    );
  }

  if (ecran === 'podium') {
    if (ecranResultatsEnErreur) return <>{barrePilotage('podium')}{ecranResultatsEnErreur}</>;
    // Séance close dont personne n'a répondu : repli historique sur l'écran de
    // fin. Sur une séance encore en cours on montre le podium tel quel — dire
    // « Session terminée » d'un quizz qui ne l'est pas serait un mensonge
    // projeté en grand.
    if (podiumRanks.length === 0 && statut === 'terminee') return <EcranFin onQuitter={() => navigate(`/home/evaluations/${idclasse}`)} />;
    return (
      <>
        {barrePilotage('podium')}
        <PodiumQCM podiumRanks={podiumRanks} />
      </>
    );
  }

  if (ecran === 'fin') return <EcranFin onQuitter={() => navigate(`/home/evaluations/${idclasse}`)} />;

  // ─── Avant lancement : distribution des cartes + QR du téléphone ───
  // Un seul écran (chantier jeton, 2026-09-04) : la liste carte ↔ élève pour
  // que chacun prenne sa carte, et le QR tant qu'aucun appareil n'est relié.
  // Une séance de groupe porte sa correspondance carte → élève dès la
  // création (`overrides_carte`, contrat back du 2026-09-21) : la liste
  // s'affiche pour elle aussi.
  if (avantLancement) {
    // Sortie AVANT lancement (revue prof 2026-09-01) : la projection s'ouvre
    // par navigation dans le MÊME onglet — sans ce bouton, l'écran d'attente
    // était un cul-de-sac. Volontairement absent pendant le quiz : quitter la
    // projection en pleine séance par mégarde laisserait la classe sans énoncé.
    const retour = (
      <button
        type="button"
        onClick={() => navigate(`/home/evaluations/${idclasse}`)}
        className="fixed top-4 left-4 z-20 px-4 py-2 rounded-full text-sm font-semibold text-white/50 hover:text-white/90 hover:bg-white/10 transition"
      >
        ← Retour
      </button>
    );
    return (
      <>
        {retour}
        <ProjectionDistribution
          titre={session?.titre}
          classeId={session?.classe}
          groupeId={session?.groupe}
          overrides={session?.overrides_carte}
          sessionId={id}
          jeton={jeton}
          relie={relie}
          onDetacher={detacher}
          erreurJeton={erreurJeton}
        />
      </>
    );
  }

  // ─── Écran d'attente ───
  if (!questionCourante) {
    return (
      <div className="h-screen w-full flex items-center justify-center p-8 overflow-hidden text-white" style={{ background: QCM_NAVY_BG }}>
        <div className="text-center space-y-6">
          <div className="mx-auto w-fit">
            <TanoProjo variant="idle" mood="idle" size={130} />
          </div>
          <p className="text-3xl font-black" style={{ fontFamily: FONT_TITRE }}>En attente de la première question…</p>
          <p className="font-semibold text-white/50">La question s'affichera automatiquement</p>
        </div>
      </div>
    );
  }

  // ─── Question active ───
  // Le bouton dans l'en-tête, à côté des compteurs (en bas, il se confondait
  // avec les noms des élèves à scanner) ; le panneau au CENTRE, par-dessus
  // l'énoncé, assez grand pour être scanné depuis le fond.
  const boutonRelier = (
    <button
      type="button"
      onClick={ouvrirRelier}
      className="flex items-center gap-1.5 mr-2 px-3 py-1.5 rounded-full text-xs font-semibold text-white/50 hover:text-white hover:bg-white/10 transition"
      style={{ border: '1px solid rgba(255,255,255,.25)' }}
    >
      <Smartphone size={13} /> Relier un téléphone
    </button>
  );
  const panneauRelierTelephone = panneauRelier ? (
    <div className="fixed inset-0 z-20 flex items-center justify-center" style={{ background: 'rgba(0,0,0,.6)' }} data-testid="relier-telephone">
      <div className="relative p-8 rounded-3xl text-center" style={{ background: 'rgba(15,23,42,.95)', border: '1px solid rgba(255,255,255,.2)' }}>
        <button
          type="button"
          onClick={() => setPanneauRelier(false)}
          aria-label="Fermer"
          className="absolute top-3 right-3 p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10"
        >
          <X size={16} />
        </button>
        <p className="text-sm font-semibold text-white/60 mb-4">La séance reprend là où elle est dès qu'un téléphone se relie.</p>
        {detachementRefuse ? (
          <p className="text-red-300 text-sm" data-testid="relier-refus">Le serveur refuse de détacher l'appareil actuel ({detachementRefuse}).</p>
        ) : refuse ? (
          <p className="text-red-300 text-sm" data-testid="relier-refus">Le serveur dit qu'un appareil est déjà relié et ne donne pas de nouveau QR.</p>
        ) : (
          <PanneauAppareil sessionId={id} jeton={jeton} relie={false} onDetacher={detacher} grand erreurJeton={erreurJeton} />
        )}
      </div>
    </div>
  ) : null;
  return (
    <div
      className="h-screen w-full flex flex-col overflow-hidden text-white"
      style={{ background: QCM_NAVY_BG, fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      {panneauRelierTelephone}
      <ProjectionHeader
        titre={session?.titre}
        questionOrdre={questionOrdre}
        totalQuestions={totalQuestions}
        nbReponses={nbReponses}
        totalEleves={totalEleves}
        action={boutonRelier}
      />

      {confettis ? <ProjConfetti /> : null}

      {phase === 'scan' ? (
        // Le scan est ouvert : l'énoncé et les choix s'en vont, il ne reste que
        // la consigne et les prénoms en grand. Deux écrans distincts, décision
        // du prof (2026-09-19) : le temps de réflexion est fini.
        <ScanScreen
          nonRepondants={nonRepondants}
          complet={complet}
          nbReponses={nbReponses}
          onReveal={handleReveal}
        />
      ) : (
        <>
          {/* Énoncé — occupe l'espace restant, police auto-ajustée, TOUT visible.
              Graisse 700 et tracking neutre (décision prof 2026-09-20, deux
              essais) : en 900 les traits d'Archivo épaississent, les BLANCS non,
              et le -1 % les rognait encore ; l'espace fine des milliers
              disparaissait et « 1 341 188 » se lisait « 1341188 » au fond de la
              salle. 800 restait trop lourd à l'œil du prof. Réglé sur la
              PROJECTION seule, le caractère de l'espace étant partagé avec le
              papier, où l'espace fine est la bonne typographie à 50 cm. En 700
              les contre-formes du 8, du 9 et du 0 restent ouvertes, ce qui
              compte quand l'élève doit lire chaque chiffre. */}
          <AutoFitText
            key={questionOrdre}
            fitKey={questionOrdre}
            minSize={16}
            maxSize={72}
            className="proj-fade-up flex-1 px-9 pt-3 font-bold"
          >
            <div
              className="leading-[1.18] text-white [&_img]:max-h-[38vh] [&_img]:object-contain [&_svg]:max-h-[38vh] [&_svg]:max-w-full"
              style={{ fontFamily: FONT_TITRE, letterSpacing: '0' }}
            >
              <RichTextBlockEleve
                // le rappel « ✏️ pense à ton brouillon » ne se projette pas : le prof est là
                texte={sansRappelBrouillon(questionCourante.texte)}
                elements={questionCourante.elements_additionnels?.elements}
                theme="dark"
              />
            </div>
          </AutoFitText>

          <ChoicesGrid key={`choix-${questionOrdre}`} choix={questionCourante.choix || []} />

          <RotationHint />

          {/* `reflexion` : rien sous les choix, personne n'est nommé tant que le
              prof n'a pas ouvert le scan. */}
          {phase === 'reveal' ? (
            <ProjectionReveal
              pctCorrect={pctCorrect}
              message={revealInfo.message}
              couleur={revealInfo.couleur}
              bonneReponse={bonneReponse}
              animated={animated}
            />
          ) : phase === 'attente' ? (
            <WaitingBand
              nonRepondants={nonRepondants}
              complet={complet}
              nbReponses={nbReponses}
              onReveal={handleReveal}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default ProjectionQCM;
